import "./TvaByQuarter.scss";

import { useEffect, useState } from "react";

import Card from "../../../../components/Card/Card";
import Spinner from "../../../../components/Utils/Spinner/Spinner";
import useLoadingStore from "../../../../stores/LoadingStore";
import { formatCurrency } from "../../../../Utils/formatCurrency";
import appFetch from "../../../../Utils/Services/Fetch/appFetch";

interface QuarterData {
  Q1: number;
  Q2: number;
  Q3: number;
  Q4: number;
}

const QUARTERS = [
  { key: "Q1" as const, label: "Q1", period: "Jan – Mar" },
  { key: "Q2" as const, label: "Q2", period: "Avr – Juin" },
  { key: "Q3" as const, label: "Q3", period: "Juil – Sep" },
  { key: "Q4" as const, label: "Q4", period: "Oct – Déc" },
];

const currentQuarter = (): number => {
  return Math.ceil((new Date().getMonth() + 1) / 3);
};

const TvaByQuarter = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarters, setQuarters] = useState<QuarterData | null>(null);
  const { loadingStates, setLoading } = useLoadingStore();

  useEffect(() => {
    const fetchData = async () => {
      setLoading("tvaByQuarter", true);
      const data = await appFetch(
        "/api/accounting/dashboard/tvaByQuarter",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ year }),
        },
      );
      if (data) {
        setQuarters(data.quarters);
      }
      setLoading("tvaByQuarter", false);
    };

    fetchData();
  }, [year]);

  const isCurrentYear = year === new Date().getFullYear();
  const activeQuarter = isCurrentYear ? currentQuarter() : null;

  return (
    <Card className="tvaByQuarter">
      <div className="tvaByQuarter__header">
        <h3 className="tvaByQuarter__title">TVA collectée par trimestre</h3>
        <div className="tvaByQuarter__yearNav">
          <button
            className="tvaByQuarter__yearBtn"
            onClick={() => setYear((y) => y - 1)}
            aria-label="Année précédente"
          >
            ‹
          </button>
          <span className="tvaByQuarter__year">{year}</span>
          <button
            className="tvaByQuarter__yearBtn"
            onClick={() => setYear((y) => y + 1)}
            aria-label="Année suivante"
          >
            ›
          </button>
        </div>
      </div>

      {loadingStates.tvaByQuarter ? (
        <Spinner />
      ) : (
        <div className="tvaByQuarter__grid">
          {QUARTERS.map(({ key, label, period }, idx) => {
            const quarterNum = idx + 1;
            const amount = quarters ? quarters[key] : 0;
            const isActive = activeQuarter === quarterNum;
            const isPast =
              activeQuarter !== null
                ? quarterNum < activeQuarter
                : year < new Date().getFullYear();

            return (
              <div
                key={key}
                className={`tvaByQuarter__quarter ${isActive ? "tvaByQuarter__quarter--active" : ""} ${isPast ? "tvaByQuarter__quarter--past" : ""}`}
              >
                <span className="tvaByQuarter__quarterLabel">{label}</span>
                <span className="tvaByQuarter__quarterPeriod">{period}</span>
                <span className="tvaByQuarter__quarterAmount">
                  {amount > 0 ? `${formatCurrency(amount)} €` : "—"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default TvaByQuarter;
